import {SmoothingType} from '../../util/math';

import {
  PlotType,
  ChartAggOption,
  ChartAreaOption,
  Mark,
  LegendPosition,
  PlotFontSizeOrAuto,
} from '../../util/plotHelpers';

import * as Query from '../../util/queryts';
import {Expression, metricsInExpression} from '../../util/expr';
import {
  toRunsDataQuery,
  QueryToRunsDataQueryParams,
  RunsDataQuery,
} from '../../containers/RunsDataLoader';
import * as Filters from '../../util/filters';
import {legendTemplateFieldNames} from '../../util/legend';
import * as _ from 'lodash';
import * as Run from '../../util/runs';
import {isNotNullOrUndefined} from '../../util/types';
import {getExpressionFields} from '../PanelExpressionOptions';
import {CHART_SAMPLES} from '../../util/constants';
import * as PanelHelpers from '../../util/panelHelpers';
export const DEFAULT_MAX_GROUP_RUNS = 100;

export const X_AXIS_LABELS: {[key: string]: string} = {
  _step: 'Step',
  _absolute_runtime: 'Relative Time (Wall)',
  _runtime: 'Relative Time (Process)',
  _timestamp: 'Wall Time',
};

export interface RunsLinePlotConfig {
  xLogScale?: boolean;
  yLogScale?: boolean;
  xAxis?: string;
  startingXAxis?: string;
  smoothingWeight?: number;
  smoothingType?: SmoothingType;
  useLocalSmoothing?: boolean;
  useGlobalSmoothingWeight?: boolean;
  ignoreOutliers?: boolean;
  showOriginalAfterSmoothing?: boolean; // Show the line and the smoothed line
  xAxisMin?: number;
  xAxisMax?: number;
  yAxisMin?: number;
  yAxisMax?: number;
  legendFields?: string[];
  legendTemplate?: string; // used to generate the default legend
  aggregate?: boolean; // user selected grouping
  aggregateMetrics?: boolean; // aggregate metrics into single metric
  groupBy?: string; // key to group by
  metrics?: string[]; // names of yAxis metrics
  metricRegex?: string; // can choose yAxis metrics by regex
  useMetricRegex?: boolean;
  yAxisAutoRange?: boolean;
  chartTitle?: string;
  xAxisTitle?: string;
  yAxisTitle?: string;
  limit?: number; // max number of runs or groups to show
  groupRunsLimit?: number;
  expressions?: string[];
  xExpression?: string; // expression for x axis
  plotType?: PlotType;
  groupAgg?: ChartAggOption;
  groupArea?: ChartAreaOption;
  colorEachMetricDifferently?: boolean; // if we have multiple metrics, override the run colors
  overrideSeriesTitles?: {[key: string]: string}; // For setting the specific names of lines
  overrideColors?: {[key: string]: {color: string; transparentColor: string}};
  overrideMarks?: {[key: string]: Mark};
  overrideLineWidths?: {[key: string]: number};
  showLegend?: boolean; // Display legend in chart (default true)
  legendPosition?: LegendPosition;
  fontSize?: PlotFontSizeOrAuto;
}

export interface Range {
  min: number | null;
  max: number | null;
}

export function runsLinePlotTransformQuery(
  query: Query.Query,
  config: RunsLinePlotConfig,
  xStepRange: Range | null,
  parsedExpressions: {
    expressions: Expression[] | undefined;
    xExpression: Expression | undefined;
  }
): RunsDataQuery {
  const singleRun = Boolean(query.runName);

  let result;
  if (singleRun) {
    result = toRunsDataQuery(query);
  } else {
    const queryToDataQueryParams: QueryToRunsDataQueryParams = {
      selectionsAsFilters: true,
    };

    if (config.metrics != null && config.metrics.length > 0) {
      const filters: Filters.Filter[] = config.metrics.map(metric => ({
        key: {section: 'keys_info', name: metric},
        op: '!=',
        value: null,
      }));
      queryToDataQueryParams.mergeFilters = Filters.Or(filters);
    }

    result = toRunsDataQuery(query, queryToDataQueryParams);
  }

  result.disabled = true;

  let legendFields = config.legendFields ?? [];
  if (config.legendTemplate != null) {
    const templateFields = legendTemplateFieldNames(config.legendTemplate);
    const extraLegendFields = _.difference(templateFields, legendFields);
    legendFields = legendFields.concat(extraLegendFields);
  }

  const displayFields = [
    ...legendFields.map(Run.keyFromString).filter(isNotNullOrUndefined),
    ...(query.grouping ?? []),
    ...(config.aggregate && config.groupBy
      ? [Run.key('config', config.groupBy)]
      : []),
  ];
  // We need to query grouping values for runsets if the runsets have grouping
  // since we do the grouping locally.
  for (const rs of query.runSets ?? []) {
    if (rs.grouping != null) {
      displayFields.push(...rs.grouping);
    }
  }

  const expressionFields = getExpressionFields(parsedExpressions);

  const extraFields = _.uniq([...displayFields, ...expressionFields]);

  result.configKeys = extraFields
    .filter(key => key.section === 'config')
    .map(key => key.name);
  result.summaryKeys = extraFields
    .filter(key => key.section === 'summary')
    .map(key => key.name);

  if (config.xAxis != null && config.metrics != null) {
    // We load the history data for the graphs
    // We sample values where an individual metric and the xAxis has values
    // Some users might prefer to sample history rows where all of the metrics
    // and the xAxis has a value, but this will cause some graphs to not display
    // if for example a user logs test data in one step and training data in another
    // and then wants to plot the two metrics in a single graph.
    result.disabled = false;
    result.history = true;

    const derivedXAxis = getDerivedXAxis(config);

    const yAxisExprKeys: string[] = [];
    for (const expr of parsedExpressions.expressions ?? []) {
      yAxisExprKeys.push(...metricsInExpression(expr));
    }
    const xAxisExprKeys: string[] =
      parsedExpressions.xExpression != null
        ? metricsInExpression(parsedExpressions.xExpression)
        : [];

    const xAxisHistoryKeys: string[] = []; // history keys we need besides the metric
    if (!getHasSystemMetrics(config)) {
      xAxisHistoryKeys.push('_step');
    }
    if (derivedXAxis === '_absolute_runtime') {
      // we calculate absolute wall time from the start of the run
      // we need both timestamp and runtime to do this
      xAxisHistoryKeys.push('_timestamp');
      xAxisHistoryKeys.push('_runtime');
    } else {
      // this is the normal case
      xAxisHistoryKeys.push(derivedXAxis);
    }
    xAxisHistoryKeys.push(...xAxisExprKeys);

    result.historySpecs = _.uniq([
      ...config.metrics,
      ...yAxisExprKeys,
      ...xAxisExprKeys,
    ]).map(metricKey => ({
      keys: _.uniq([...xAxisHistoryKeys, metricKey]),
      samples: CHART_SAMPLES,
      ...(xStepRange != null
        ? {
            minStep: xStepRange.min,
            maxStep: xStepRange.max,
          }
        : {}),
    }));

    result.page = {
      size: singleRun ? 1 : config.limit || 10,
    };

    if (PanelHelpers.isGrouped(query, config)) {
      // We need the metadata for grouping because we do it locally
      // TODO: move grouping to server
      // result.disableMeta = false;

      // optionally compute group statistics over all runs instead of sub-sampling
      result.page.size = config.groupRunsLimit || DEFAULT_MAX_GROUP_RUNS;
      // Disable grouping for this query, we'll do it ourselves.
      result.queries = result.queries.map(q => ({...q, grouping: []}));
    }
  }

  return result;
}

export function defaultTitle(
  config: Pick<RunsLinePlotConfig, 'metrics' | 'expressions'>
): string {
  const metricsInUse = Boolean(config.expressions?.[0])
    ? config.expressions
    : config.metrics;

  return metricsInUse?.join(', ') ?? '';
}

export function getHasSystemMetrics(
  config: Pick<RunsLinePlotConfig, 'metrics'>
): boolean {
  if (config.metrics == null) {
    return false;
  }
  return config.metrics.some(metric => metric.startsWith('system/'));
}

export // system metrics charts can't have _step or null xAxes, so for those
// charts we fall back to _runtime as the default
function getDerivedXAxis(
  config: Pick<RunsLinePlotConfig, 'metrics' | 'xAxis'>
): string {
  if (getHasSystemMetrics(config)) {
    // system metrics have no '_step' to log by, so we use '_runtime'
    // as the default instead
    if (config.xAxis == null || config.xAxis === '_step') {
      return '_runtime';
    }

    return config.xAxis;
  }

  // all non system-metrics charts should use '_step' as the default
  if (config.xAxis == null) {
    return '_step';
  }

  return config.xAxis;
}

export function getTitleFromConfig(config: RunsLinePlotConfig): string {
  return config.chartTitle || defaultTitle(config);
}
